import './ListPage.scss';
import { Button, Icon, SimpleNumberCard, Checkbox } from '@design-system/index';
import { ArchiveLine, ArchiveFilled, MembersListLine, AddDashed, UnarchiveFilled, UnarchiveLine, Warning } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { MemberTable } from '@services/member/member/member.types';
import { ColumnDef } from '@tanstack/react-table';
import { formatDate } from '@utils/dates';
import { CONTRIBUTION_TYPE } from '@utils/enumTranslation';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { useListPageVM } from './useListPageVM';
import { List } from '@services/member/list/list.types';
import { Permission } from '@components/Permission/Permission';

export default function ListPage() {
    const { handleToggleArchiveMember, handleToggleArchiveMembers, handleMetadata, navigate, columnHelper, isArchived, stateCodes, organizationCodes, serviceCodes, toggleArchived, totalMembers, exportMutationHook, mutationHook, isLoading, needReload, user } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            meta: {
                role: 'admin'
            },
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
                
            )
        }),

        columnHelper.accessor('cosemNumber', {
            header: 'N°COSEM',
            id: 'cosemNumber',
            enableSorting: true,
            meta: {
                clickable: true,
            },
            cell: (info) => (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center'}}>                    
                    {!info.row.original.contributionUpToDate && (
                        <Icon icon={<Warning />} size={18} color="error" />
                    )}
                    <>{info.getValue()}</>
                </div>
            )
        }),

        columnHelper.accessor((row) => row.administrativeInformation.registrationNumber, {
            id: 'administrativeInformation.registrationNumber',
            header: 'Matricule',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor((row) => row.administrativeInformation.registeredAt, {
            id: 'administrativeInformation.registeredAt',
            header: "Date d'adhésion",
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{formatDate(info.getValue())}</>
        }),
        columnHelper.accessor((row) => row.personalInformation.lastname, {
            id: 'personalInformation.lastname',
            header: 'Nom',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue().toUpperCase()}</>
        }),
        columnHelper.accessor((row) => row.personalInformation.firstname, {
            id: 'personalInformation.firstname',
            header: 'Prénom',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{info.getValue().toUpperCase()}</>
        }),
        columnHelper.display({
            id: 'archive',
            size: 0,
            header: isArchived ? 'Désarchiver' : 'Archiver',
            meta: {
                role: 'admin',
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (<div style={{display: 'flex', justifyContent: 'center'}}>
                <Button
                    variant="icon"
                    color="primary"
                    size="small"
                    hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                    icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                    onClick={() => handleToggleArchiveMember(info.row.original.id, !isArchived)}
                />
            </div>)
            }
        })
    ] as ColumnDef<MemberTable>[];


    return (        
        <div className="members-table-container">
            <div className="members-table-container-header">
                <div className="members-table-container-header-left">
                    <SimpleNumberCard labelForSingle={isArchived ? 'Adhérent archivé' : 'Adhérent'} labelForMultiple={isArchived ? 'Adhérents archivés' : 'Adhérents'} value={totalMembers} />
                    <LargeIconButton icon={<AddDashed />} label="Nouvel adhérent" onClick={() => navigate('/members-management/members/add')} />
                </div>
                <Permission role="admin">
                    <Button variant="secondary" onClick={toggleArchived}>
                        {isArchived ? 'Actifs' : 'Archivés'}
                    </Button>
                </Permission>
            </div>
            <MutationTable
                key={isArchived ? 'archived' : 'active'}
                persistKey={isArchived ? 'members-archived' : 'members'}
                columns={columns}
                forceLoading={isLoading}
                selectable={user?.role === 'admin'}
                needReload={needReload}
                mutationHook={mutationHook}
                onRowClick={(row) => navigate(`/members-management/members/${row.id}/details`)}
                defaultSorting={{
                    field: 'cosemNumber',
                    direction: 'desc'
                }}
                deleting={{
                    enabled: isArchived,
                    entity: 'member',
                }}
                exportable={true}
                exportMutationHook={exportMutationHook}
                headerTitle={isArchived ? 'Liste des adhérents archivés' : 'Liste des adhérents'}
                headerIcon={<MembersListLine />}
                onMetadata={handleMetadata}
                filters={[
                    {
                        id: "administrativeInformation.registeredAt",
                        label: "Date d'adhésion",
                        type: "date",
                        comparatorTypes: ['isBetween', 'isBefore', 'isAfter', 'equals']
                    },
                  /*  {
                        id: "personalInformation.firstname",
                        label: "Prénom",
                        type: "string",
                        comparatorTypes: ['contains', 'equals', 'startsWith', 'endsWith']
                    },*/
                    {
                        id: "administrativeInformation.stateCode",
                        label: "Code état",
                        type: "select",
                        options: stateCodes.map((state: List) => ({label: state.label, value: state.code}))
                    },
                    {
                        id: "hasContributionUpToDate",
                        label: "Cotisation à jour",
                        type: "boolean"
                    },
                    {
                        id: 'administrativeInformation.contributionType',
                        label: 'Type de cotisation',
                        type: 'multiSelect',
                        options: Object.entries(CONTRIBUTION_TYPE).map(([value, label]) => ({ label, value }))
                    },
                    {
                        id: 'cosemNumber',
                        label: 'N° Cosem',
                        type: 'string',
                        comparatorTypes: ['contains', 'equals', 'startsWith']
                    },
                    {
                        id: 'administrativeInformation.registrationNumber',
                        label: 'Matricule',
                        type: 'string',
                        comparatorTypes: ['contains', 'equals', 'startsWith']
                    },
                    {
                        id: 'administrativeInformation.organization',
                        label: 'Organisme',
                        type: 'select',
                        options: organizationCodes.map((org: List) => ({ label: org.label, value: org.code }))
                    },
                    {
                        id: 'administrativeInformation.serviceCode',
                        label: 'Code service',
                        type: 'multiSelect',
                        options: serviceCodes.map((service: List) => ({ label: service.label, value: service.code }))
                    },
                    {
                        id: 'isArchived',
                        label: 'Archivé',
                        type: 'boolean'
                    },
                ]}
                footer={ 
                    {
                        actions: [
                            {
                                role: 'admin',
                                label: isArchived ? 'Désarchiver' : "Archiver",
                                icon: <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />,
                                hoverIcon: <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />,
                                onClick: (lines: MemberTable[]) => handleToggleArchiveMembers(lines, !isArchived)
                            }
                        ]
                    }
                }
            />
        </div>
    );
};
